import { useState } from 'react';
import { Input, InputNumber } from 'antd';

/**
 * Inline inputs that save on blur / Enter, only when the value changed.
 * They keep a local draft: give them a `key` derived from the saved value so they reset after a save.
 * When `onCommit` rejects, the draft goes back to the saved value.
 */

interface CommitNumberInputProps {
  value: number | null | undefined;
  onCommit: (value: number | null) => Promise<unknown>;
  /** Empty means null (e.g. "not in this plan"). Otherwise an empty input reverts. */
  nullable?: boolean;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  suffix?: string;
  placeholder?: string;
  width?: number;
}

export function CommitNumberInput({
  value,
  onCommit,
  nullable,
  min = 0,
  max,
  step,
  precision,
  suffix,
  placeholder,
  width = 100,
}: CommitNumberInputProps) {
  const saved = value ?? null;
  const [draft, setDraft] = useState<number | null>(saved);

  const commit = () => {
    if (draft === saved) return;
    if (draft == null && !nullable) {
      setDraft(saved);
      return;
    }
    onCommit(draft).catch(() => setDraft(saved));
  };

  return (
    <InputNumber<number>
      size="small"
      value={draft}
      onChange={(v) => setDraft(v ?? null)}
      onBlur={commit}
      onPressEnter={commit}
      min={min}
      max={max}
      step={step}
      precision={precision}
      suffix={suffix}
      placeholder={placeholder}
      style={{ width }}
    />
  );
}

interface CommitTextInputProps {
  value: string | null | undefined;
  onCommit: (value: string | null) => Promise<unknown>;
  /** Empty means null. Otherwise an empty input reverts. */
  nullable?: boolean;
  maxLength?: number;
  placeholder?: string;
  width?: number | string;
}

export function CommitTextInput({
  value,
  onCommit,
  nullable,
  maxLength = 150,
  placeholder,
  width = '100%',
}: CommitTextInputProps) {
  const saved = value ?? '';
  const [draft, setDraft] = useState(saved);

  const commit = () => {
    const next = draft.trim();
    if (next === saved.trim()) return;
    if (!next && !nullable) {
      setDraft(saved);
      return;
    }
    onCommit(next || null).catch(() => setDraft(saved));
  };

  return (
    <Input
      size="small"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
      maxLength={maxLength}
      placeholder={placeholder}
      style={{ width }}
    />
  );
}
